import React, { useState, useEffect } from 'react';
import { useApp } from '@/contexts/AppContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { 
  Users, 
  UserPlus, 
  Trash2, 
  User, 
  Shield, 
  ShieldCheck, 
  ShieldOff, 
  Check, 
  X,
  Loader2
} from 'lucide-react';
import toast from 'react-hot-toast';
import { supabase } from '@/lib/supabase';

interface StaffUser {
  id: string;
  email: string;
  role: 'admin' | 'manager' | 'staff';
  created_at: string;
}

export const UserManagement: React.FC = () => {
  const { currentUser, hasPermission } = useApp();
  const [users, setUsers] = useState<StaffUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserRole, setNewUserRole] = useState<'admin' | 'manager' | 'staff'>('staff');
  const [newUserPassword, setNewUserPassword] = useState('');

  // Fetch all staff users
  const loadUsers = async () => {
    try {
      const { data, error } = await supabase
        .from('staff')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setUsers(data || []);
    } catch (error) {
      console.error('Error loading users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!hasPermission('manage_staff')) {
      toast.error('You do not have permission to manage staff');
      return;
    }

    try {
      setLoading(true);

      // Step 1: Create user with standard signup
      const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
        email: newUserEmail,
        password: newUserPassword,
        options: { 
          emailRedirectTo: window.location.origin,
        }
      });

      if (signUpError || !signUpData.user) {
        throw new Error(`Signup failed: ${signUpError?.message || 'Unknown error'}`);
      }

      const userId = signUpData.user.id;

      // Step 2: Insert into staff table with the appropriate role
      const { error: staffError } = await supabase
        .from('staff')
        .upsert({
          id: userId,
          email: newUserEmail,
          role: newUserRole,
          created_at: new Date().toISOString()
        });

      if (staffError) {
        throw new Error(`Staff record creation failed: ${staffError.message}`);
      }

      toast.success(`User ${newUserEmail} created successfully with ${newUserRole} role`);
      setNewUserEmail('');
      setNewUserPassword('');
      setNewUserRole('staff');
      loadUsers();
    } catch (error) {
      console.error('Error creating user:', error);
      toast.error(`Failed to create user: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (userId: string, userEmail: string) => {
    if (!hasPermission('manage_staff')) {
      toast.error('You do not have permission to manage staff');
      return;
    }

    if (window.confirm(`Are you sure you want to delete ${userEmail}?`)) {
      try {
        setLoading(true);
        
        // First delete from staff table to avoid foreign key issues
        const { error: staffError } = await supabase
          .from('staff')
          .delete()
          .eq('id', userId);
        
        if (staffError) throw staffError;
        
        // Then attempt to delete from auth
        try {
          const { error } = await supabase.auth.admin.deleteUser(userId);
          if (error) console.warn('Could not delete from auth directly:', error);
        } catch (authError) {
          console.warn('Auth deletion failed, but staff record was removed:', authError);
        }
        
        toast.success('User deleted successfully');
        loadUsers();
      } catch (error) {
        console.error('Error deleting user:', error);
        toast.error(`Failed to delete user: ${error instanceof Error ? error.message : 'Unknown error'}`);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleUpdateRole = async (userId: string, newRole: 'admin' | 'manager' | 'staff') => {
    if (!hasPermission('manage_staff')) {
      toast.error('You do not have permission to manage staff');
      return;
    }

    try {
      setLoading(true);
      
      // Use RPC function to update staff role (bypasses RLS)
      const { error: rpcError } = await supabase.rpc('update_staff_role', {
        p_user_id: userId,
        p_role: newRole
      });

      if (rpcError) {
        console.error('RPC error:', rpcError);
        throw new Error(`Failed to update role: ${rpcError.message}`);
      }
      
      toast.success('Role updated successfully');
      loadUsers();
    } catch (error) {
      console.error('Error updating role:', error);
      toast.error(`Failed to update role: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  if (!hasPermission('manage_staff')) {
    return null;
  }

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle>User Management</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleAddUser} className="space-y-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Input
              placeholder="Email"
              type="email"
              value={newUserEmail}
              onChange={(e) => setNewUserEmail(e.target.value)}
              required
            />
            <Input
              placeholder="Password"
              type="password"
              value={newUserPassword}
              onChange={(e) => setNewUserPassword(e.target.value)}
              required
            />
            <Select
              value={newUserRole}
              onValueChange={(value: 'admin' | 'manager' | 'staff') => setNewUserRole(value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="manager">Manager</SelectItem>
                <SelectItem value="staff">Staff</SelectItem>
              </SelectContent>
            </Select>
            <Button type="submit" disabled={loading} className="bg-red-600 hover:bg-red-700">
              Add User
            </Button>
          </div>
        </form>

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Email</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Created</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map((user) => (
              <TableRow key={user.id}>
                <TableCell>{user.email}</TableCell>
                <TableCell>
                  <Select
                    value={user.role}
                    onValueChange={(value: 'admin' | 'manager' | 'staff') => 
                      handleUpdateRole(user.id, value)
                    }
                    disabled={user.email === currentUser?.username}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="manager">Manager</SelectItem>
                      <SelectItem value="staff">Staff</SelectItem>
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell>
                  {new Date(user.created_at).toLocaleDateString()}
                </TableCell>
                <TableCell>
                  <Button
                    variant="destructive"
                    onClick={() => handleDeleteUser(user.id, user.email)}
                    disabled={user.email === currentUser?.username || loading}
                  >
                    Delete
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}; 